import { createContext, useContext, useEffect, useState } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientRecord {
  id: string | null
  email: string
  name?: string
  company?: string
  approved?: boolean
  last_seen?: string
  last_login?: string
  [key: string]: unknown
}

export interface ContractorRecord {
  id: string
  email: string
  name?: string
  last_seen?: string
  last_login?: string
  [key: string]: unknown
}

interface AuthContextValue {
  user: User | null
  client: ClientRecord | null
  contractor: ContractorRecord | null
  isAdmin: boolean
  isContractor: boolean
  isPending: boolean
  loading: boolean
  isPreview: boolean
  previewClient: ClientRecord | null
  previewContractor: ContractorRecord | null
  startPreview: (type: 'client' | 'contractor', data: ClientRecord | ContractorRecord) => void
  exitPreview: () => void
  signInWithPassword: (email: string, password: string) => Promise<{ error: unknown }>
  signUp: (name: string, email: string, password: string) => Promise<{ data?: unknown; error?: unknown }>
  sendPasswordResetEmail: (email: string) => Promise<{ error: unknown }>
  sendMagicLink: (email: string) => Promise<{ error: unknown }>
  signOut: () => Promise<void>
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [client, setClient] = useState<ClientRecord | null>(null)
  const [contractor, setContractor] = useState<ContractorRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [adminEmails, setAdminEmails] = useState<string[]>([
    process.env.REACT_APP_ADMIN_EMAIL || 'efren@abstract-creative.com',
    'angela.n.carrasco@gmail.com',
  ])
  const [previewClient, setPreviewClient] = useState<ClientRecord | null>(() => {
    try { return JSON.parse(sessionStorage.getItem('ac_preview_client') || 'null') } catch { return null }
  })
  const [previewContractor, setPreviewContractor] = useState<ContractorRecord | null>(() => {
    try { return JSON.parse(sessionStorage.getItem('ac_preview_contractor') || 'null') } catch { return null }
  })

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchUserRole(session.user.email!)
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchUserRole(session.user.email!)
      } else {
        setClient(null)
        setContractor(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchUserRole(email: string) {
    const now = new Date().toISOString()
    try {
      const { data: clientData } = await supabase
        .from('clients')
        .select('*')
        .eq('email', email)
        .maybeSingle()

      if (clientData) {
        setClient(clientData)
        setContractor(null)
        supabase.from('clients').update({ last_seen: now, last_login: now }).eq('id', clientData.id).then(() => {})
        return
      }

      const { data: contractorData } = await supabase
        .from('contractors')
        .select('*')
        .eq('email', email)
        .maybeSingle()

      if (contractorData) {
        setContractor(contractorData)
        setClient(null)
        supabase.from('contractors').update({ last_seen: now, last_login: now }).eq('id', contractorData.id).then(() => {})
        return
      }

      supabase.from('admin_users').select('email').then(({ data }) => {
        if (data?.length) setAdminEmails(data.map((r: { email: string }) => r.email))
      })
      setClient({ id: null, email })
      setContractor(null)
    } catch (err) {
      console.error('fetchUserRole error:', err)
      setClient({ id: null, email, name: 'User' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const id = setInterval(() => {
      const ts = new Date().toISOString()
      if (client?.id) {
        supabase.from('clients').update({ last_seen: ts }).eq('id', client.id).then(() => {})
      } else if (contractor?.id) {
        supabase.from('contractors').update({ last_seen: ts }).eq('id', contractor.id).then(() => {})
      }
    }, 2 * 60 * 1000)
    return () => clearInterval(id)
  }, [client?.id, contractor?.id])

  async function signInWithPassword(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  async function signUp(name: string, email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) return { error }
    await supabase.from('clients').insert({ name, email, approved: false })
    return { data }
  }

  async function sendPasswordResetEmail(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error }
  }

  async function sendMagicLink(email: string) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    })
    return { error }
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setClient(null)
    setContractor(null)
  }

  const isAdmin = adminEmails.includes(user?.email ?? '')
  const isContractor = !isAdmin && !!contractor?.id
  const isPending = !isAdmin && !isContractor && !!user && client?.approved === false

  const isPreview = isAdmin && (!!previewClient || !!previewContractor)
  const effectiveClient = isAdmin && previewClient ? previewClient : client
  const effectiveContractor = isAdmin && previewContractor ? previewContractor : contractor

  function startPreview(type: 'client' | 'contractor', data: ClientRecord | ContractorRecord) {
    if (type === 'client') {
      sessionStorage.setItem('ac_preview_client', JSON.stringify(data))
      sessionStorage.removeItem('ac_preview_contractor')
      setPreviewClient(data as ClientRecord)
      setPreviewContractor(null)
    } else {
      sessionStorage.setItem('ac_preview_contractor', JSON.stringify(data))
      sessionStorage.removeItem('ac_preview_client')
      setPreviewContractor(data as ContractorRecord)
      setPreviewClient(null)
    }
  }

  function exitPreview() {
    sessionStorage.removeItem('ac_preview_client')
    sessionStorage.removeItem('ac_preview_contractor')
    setPreviewClient(null)
    setPreviewContractor(null)
  }

  return (
    <AuthContext.Provider value={{
      user,
      client: effectiveClient,
      contractor: effectiveContractor,
      isAdmin, isContractor, isPending, loading,
      isPreview, previewClient, previewContractor,
      startPreview, exitPreview,
      signInWithPassword, signUp, sendPasswordResetEmail, sendMagicLink, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
